import { isValidJSON } from '../smart-flow/utils'
import {
  requestAbortedMsg,
  validImageInput,
  copyIconString,
  maximizeIconString,
  downloadIconString,
  chevronLeftIconString,
  chevronRightIconString
} from './constants'
import { ChatBubbleItem, ChatRawMessageItem, chatMessageSchema } from './types'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
//@ts-ignore
import Prism from 'prismjs'
//@ts-ignore
import katex from 'katex'
import { checkIsDrawerMode, fetchDomain, validateImageUrl } from './helpers'
import { normalizeCitationFileName } from '../shared/citation-preview/citationNormalizer'
import { convertVegaLiteToChartSpec } from '../frontend-tools/tools/visualization/vegaLiteAdapter'

/*
 * Escapes HTML-significant characters so untrusted/AI-generated strings (e.g. file names)
 * can be safely interpolated into HTML markup and attribute values without breaking out of
 * their intended tag/attribute context. Must be used for any dynamic value injected into the
 * raw HTML strings built by the `marked` renderer below (DOMPurify runs after this string is
 * parsed, so unescaped special characters can still corrupt the surrounding markup before
 * sanitization ever sees it).
 */
export function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/*
 * Escapes a value for safe use inside a single-quoted inline JS string literal that itself
 * lives inside an HTML attribute (e.g. onclick="doThing('${value}')"). Backslashes and single
 * quotes are escaped first so the value can't break out of the JS string, then the result is
 * HTML-attribute-escaped so it can't break out of the surrounding double-quoted HTML attribute
 * either. Without this, a single stray quote character anywhere earlier in a streamed message
 * can desynchronize the HTML parser for everything rendered afterwards in that message.
 */
export function escapeForInlineJsAttr(value: string): string {
  return escapeHtml(
    String(value ?? '')
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
  )
}

/*
 * Function to detect and convert URLs within a string into clickable links.
 * It wraps each link matched with anchor tags and applies safe href attributes.
 */
export function convertToAnchorTags(htmlString: string) {
  // Regular expression to match links, excluding any HTML tags at the end
  // Since response from bot is plain text, sometimes line breaks and other html tags are included in the response for readability.
  const linkRegex =
    /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-/]))?(?=(<br|<p|<div|<span)\s*\/>|$)/g

  const result = htmlString.replace(linkRegex, function (link) {
    // Parse URL first -- URL class handles cybersecurity concerns related to URL parsing and manipulation
    const normalizedHref = new URL(link).toString()

    // Replace each link with anchor tags
    return `<a href="${normalizedHref}">${link}</a>`
  })

  return result
}

/*
 * Function to render the date and/or time of a message.
 */
export function timestampToDateString(timestamp: number, alwaysShowTime = false) {
  const date = new Date(timestamp)
  const dateString = date.toLocaleDateString([], {
    month: 'numeric',
    day: 'numeric'
  })
  const timeString = date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  })

  return date.toDateString() !== new Date().toDateString()
    ? alwaysShowTime
      ? `${dateString} ${timeString}` // if the date is not today and we are always showing the time, show the date and time
      : dateString // if the date is not today and we are not always showing the time, only show the date
    : timeString // if the date is today, only show the time
}

/*
 * Function to create a command link
 */
export function createCommandLink(command: string) {
  const escapedCommand = encodeURIComponent(command)
  const createCommandLink = `<span style="text-decoration: underline; cursor: pointer" data-command="${escapedCommand}" onclick="(function(){ let chatInput = document.getElementById('chat-input'); chatInput.value = decodeURIComponent('${escapedCommand}'); chatInput.focus(); return false; })();return false;">${command}</span>`
  return createCommandLink
}

/*
 * Function to format chat text content to remove any html tags from it.
 */
export function formatChatTextContent(messageContent: string) {
  const contentAsString = messageContent
    .trim()
    .replace(/^sk:\/\/.*$/gm, (match: string) => createCommandLink(match))
    .replace(/^!sk:.*$/gm, (match: string) => createCommandLink(match))
  return contentAsString
}

export const convertPascalCaseToWords = (pascalCaseString: string) => {
  return pascalCaseString.replace(/([A-Z]+)(?![A-Z])/g, ' $1').trim()
}

export const getFileType = (fileName: string) => {
  if (fileName.includes('.')) {
    return fileName.split('.').pop()?.toUpperCase()
  } else {
    return 'DOC'
  }
}

export const sentenceCase = (str: string) => {
  if (str === null || str === '') return false
  else str = str.toString()

  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export const getClassForFileType = (fileType = '') => {
  let className = 'bg-light-warning'
  switch (fileType) {
    case 'PDF':
      className = 'bg-light-secondary'
      break
    case 'PPTX':
      className = 'bg-light-danger'
      break
    case 'XLSX':
    case 'CSV':
      className = 'bg-light-success'
      break
    case 'TXT':
      className = 'bg-light-primary'
      break
    case 'DOCX':
      className = 'bg-light-info'
      break
    default:
      break
  }
  return className
}

export const getColorForFileType = (fileType = '') => {
  let className = '--dsm-warning-color'
  switch (fileType) {
    case 'PDF':
      className = '--dsm-secondary-color'
      break
    case 'PPTX':
    case 'PPT':
      className = '--dsm-danger-color'
      break
    case 'XLSX':
    case 'XLS':
    case 'CSV':
      className = '--dsm-success-color'
      break
    case 'TXT':
      className = '--dsm-primary-color'
      break
    case 'DOCX':
    case 'DOC':
      className = '--dsm-info-color'
      break
    default:
      break
  }
  return className
}

export enum ArtifactFileType {
  PDF = 'PDF',
  PPTX = 'PPTX',
  DOCX = 'DOCX',
  XLSX = 'XLSX',
  CSV = 'CSV',
  TXT = 'TXT',
  DOC = 'DOC',
  PPT = 'PPT',
  XLS = 'XLS'
}

// Raw SVG strings for file type icons, used inside `marked`'s HTML string renderer (artifact
// cards). These mirror the React icon components in ../icons/Icons.tsx (PdfFileIcon, DocxFileIcon,
// etc.) and the getFileIcon() helper in helpers.tsx, but are kept as raw strings here because
// marked outputs HTML, not React elements.
const fileIconSvgs: Record<string, string> = {
  PDF: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.60369 0H9.29034L14.9575 5.90705V17.3963C14.9575 18.8356 13.7931 20 12.3588 20H2.60369C1.16441 20 0 18.8356 0 17.3963V2.60369C0 1.16441 1.16439 0 2.60369 0Z" fill="#E5252A"/><path opacity="0.302" fill-rule="evenodd" clip-rule="evenodd" d="M9.2854 0V5.86207H14.9576L9.2854 0Z" fill="white"/><path d="M2.89355 14.9227V11.2695H4.44778C4.83259 11.2695 5.13745 11.3745 5.36733 11.5894C5.59721 11.7993 5.71216 12.0841 5.71216 12.4389C5.71216 12.7938 5.59721 13.0786 5.36733 13.2885C5.13745 13.5034 4.83259 13.6084 4.44778 13.6084H3.82809V14.9227H2.89355ZM3.82809 12.8138H4.34284C4.48276 12.8138 4.59271 12.7838 4.66768 12.7138C4.74263 12.6489 4.78263 12.5589 4.78263 12.439C4.78263 12.319 4.74266 12.2291 4.66768 12.1641C4.59273 12.0941 4.48278 12.0642 4.34284 12.0642H3.82809V12.8138ZM6.09695 14.9227V11.2695H7.3913C7.64617 11.2695 7.88606 11.3045 8.11094 11.3795C8.33582 11.4544 8.54073 11.5594 8.72063 11.7043C8.90055 11.8442 9.04547 12.0341 9.15042 12.274C9.25036 12.5139 9.30535 12.7888 9.30535 13.0986C9.30535 13.4035 9.25039 13.6783 9.15042 13.9182C9.04547 14.1581 8.90055 14.348 8.72063 14.4879C8.54071 14.6328 8.33582 14.7378 8.11094 14.8128C7.88606 14.8877 7.64617 14.9227 7.3913 14.9227H6.09695ZM7.01149 14.1281H7.28135C7.42628 14.1281 7.56122 14.1131 7.68615 14.0781C7.80608 14.0432 7.92104 13.9882 8.03098 13.9132C8.13593 13.8383 8.22089 13.7333 8.28085 13.5934C8.34082 13.4535 8.37082 13.2885 8.37082 13.0986C8.37082 12.9037 8.34082 12.7388 8.28085 12.5989C8.22089 12.459 8.13593 12.354 8.03098 12.279C7.92104 12.2041 7.80611 12.1491 7.68615 12.1141C7.56122 12.0792 7.42628 12.0641 7.28135 12.0641H7.01149V14.1281ZM9.77512 14.9227V11.2695H12.3738V12.0641H10.7096V12.6488H12.039V13.4385H10.7096V14.9227H9.77512Z" fill="white"/></svg>',
  PPTX: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.60369 0H9.29034L14.9575 5.90705V17.3963C14.9575 18.8356 13.7931 20 12.3588 20H2.60369C1.16441 20 0 18.8356 0 17.3963V2.60369C0 1.16441 1.16439 0 2.60369 0Z" fill="#D24625"/><path opacity="0.302" fill-rule="evenodd" clip-rule="evenodd" d="M9.2854 0V5.86207H14.9576L9.2854 0Z" fill="white"/><text x="7.5" y="15" fill="white" font-size="4" font-weight="700" font-family="sans-serif" text-anchor="middle">PPTX</text></svg>',
  DOCX: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 20" fill="none"><path d="M2.60177 0H9.2936L14.9582 5.90996V17.3982C14.9582 18.8353 13.7935 20 12.3564 20H2.60177C1.16472 20 0 18.8353 0 17.3982V2.60177C0 1.16474 1.16472 0 2.60177 0Z" fill="#0263D1"/><path opacity="0.302" fill-rule="evenodd" clip-rule="evenodd" d="M9.28552 0V5.86141H14.9582L9.28552 0Z" fill="white"/><path d="M2.41577 14.2705V10.9596H3.5886C3.82316 10.9596 4.04156 10.9947 4.24377 11.0594C4.44598 11.1268 4.62932 11.2239 4.7938 11.3533C4.95826 11.4827 5.08769 11.6553 5.18205 11.8709C5.27641 12.0866 5.32495 12.3347 5.32495 12.6151C5.32495 12.8955 5.27641 13.1435 5.18205 13.3592C5.08769 13.5749 4.95826 13.7474 4.7938 13.8768C4.62934 14.0062 4.44598 14.1033 4.24377 14.1707C4.04156 14.2354 3.82318 14.2705 3.5886 14.2705H2.41577ZM3.24349 13.5506H3.48883C3.62095 13.5506 3.74497 13.5345 3.8555 13.5048C3.96874 13.4724 4.07118 13.4212 4.16824 13.3538C4.2653 13.2864 4.34079 13.1893 4.39471 13.0626C4.45134 12.9386 4.47828 12.7876 4.47828 12.6151C4.47828 12.4425 4.45132 12.2915 4.39471 12.1648C4.34079 12.0408 4.2653 11.9437 4.16824 11.8763C4.07118 11.8062 3.96874 11.7577 3.8555 11.7253C3.74497 11.6957 3.62095 11.6795 3.48883 11.6795H3.24349V13.5506ZM7.33356 14.3082C6.83478 14.3082 6.42227 14.1465 6.09604 13.8256C5.7698 13.5048 5.60802 13.1004 5.60802 12.6151C5.60802 12.1298 5.7698 11.7254 6.09604 11.4045C6.42227 11.0837 6.83478 10.9219 7.33356 10.9219C7.82425 10.9219 8.23137 11.0837 8.55761 11.4045C8.88114 11.7254 9.04291 12.1298 9.04291 12.6151C9.04291 13.1004 8.88114 13.5048 8.55761 13.8256C8.23137 14.1465 7.82425 14.3082 7.33356 14.3082ZM6.69726 13.3134C6.86172 13.4967 7.07201 13.5884 7.32815 13.5884C7.58428 13.5884 7.79188 13.4967 7.95636 13.3134C8.12082 13.1273 8.2017 12.8955 8.2017 12.6151C8.2017 12.3347 8.12082 12.1028 7.95636 11.9168C7.7919 11.7334 7.58428 11.6417 7.32815 11.6417C7.07201 11.6417 6.86172 11.7334 6.69726 11.9168C6.5328 12.1028 6.44921 12.3347 6.44921 12.6151C6.44921 12.8955 6.5328 13.1273 6.69726 13.3134ZM11.0111 14.3082C10.5285 14.3082 10.1267 14.1573 9.80862 13.8607C9.48777 13.5614 9.32872 13.1462 9.32872 12.6151C9.32872 12.0866 9.4905 11.6714 9.81403 11.3721C10.1403 11.0729 10.5366 10.9219 11.0111 10.9219C11.4398 10.9219 11.7903 11.027 12.068 11.24C12.343 11.4503 12.5021 11.7307 12.5425 12.0812L11.7067 12.2511C11.6717 12.0677 11.5881 11.9195 11.4587 11.8089C11.3293 11.6983 11.1783 11.6417 11.0057 11.6417C10.7684 11.6417 10.5716 11.7253 10.4125 11.8952C10.2535 12.0677 10.1726 12.305 10.1726 12.615C10.1726 12.9251 10.2535 13.1624 10.4098 13.3322C10.5689 13.5048 10.7657 13.5884 11.0057 13.5884C11.1782 13.5884 11.3265 13.5398 11.4479 13.4428C11.5692 13.3457 11.6447 13.2163 11.677 13.0545L12.5317 13.2487C12.4535 13.583 12.281 13.8418 12.0113 14.0278C11.7444 14.2139 11.4101 14.3082 11.0111 14.3082Z" fill="white"/></svg>',
  XLSX: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.59853 0H9.29196L14.9562 5.91241V17.3942C14.9562 18.8321 13.7956 20 12.3577 20H2.59853C1.16788 20 0 18.8321 0 17.3942V2.59853C0 1.16788 1.16788 0 2.59853 0Z" fill="#00733B"/><path opacity="0.302" fill-rule="evenodd" clip-rule="evenodd" d="M9.28467 0V5.86131H14.9562L9.28467 0Z" fill="white"/><path d="M6.21901 14.5621H5.24822L4.58399 13.4454L3.91975 14.5621H2.94165L4.09495 12.6205L3.08036 10.9344H4.05843L4.58397 11.8103L5.10221 10.9344H6.0803L5.07299 12.6278L6.21901 14.5621ZM6.56208 14.5621V10.9344H7.48908V13.7738H9.06572V14.5621H6.56208ZM10.5986 14.6059C10.2336 14.6059 9.91244 14.4891 9.63508 14.2629C9.36499 14.0439 9.21173 13.7665 9.18982 13.4381L9.98545 13.2045C10.0074 13.3797 10.0803 13.533 10.2044 13.6497C10.3358 13.7665 10.4818 13.8249 10.6424 13.8249C10.7738 13.8249 10.8833 13.7957 10.9708 13.7373C11.0511 13.6789 11.0949 13.5987 11.0949 13.5038C11.0949 13.4235 11.0584 13.3578 10.9927 13.2994C10.927 13.2483 10.8394 13.2045 10.7373 13.168C10.6351 13.1315 10.511 13.095 10.3869 13.0658C10.2555 13.0293 10.1241 12.9855 10 12.9271C9.86865 12.876 9.75185 12.8103 9.64966 12.7301C9.54016 12.657 9.45988 12.5476 9.39418 12.4089C9.32848 12.2775 9.29199 12.1169 9.29199 11.9344C9.29199 11.6352 9.41606 11.387 9.67154 11.1899C9.92702 10.9855 10.2336 10.8906 10.5912 10.8906C10.9489 10.8906 11.2628 10.9782 11.5255 11.1461C11.7883 11.3213 11.9562 11.5476 12.0219 11.8249L11.1898 12.1753C11.1533 12.022 11.0803 11.8979 10.9781 11.803C10.8759 11.7155 10.7445 11.6644 10.5912 11.6644C10.4744 11.6644 10.3869 11.6936 10.3139 11.7374C10.2482 11.7812 10.219 11.8469 10.219 11.9271C10.219 12.0002 10.2628 12.0658 10.3504 12.1169C10.438 12.1607 10.5548 12.1972 10.6861 12.2191C10.8175 12.241 10.9635 12.2775 11.1168 12.3286C11.2774 12.387 11.4161 12.4454 11.5547 12.5257C11.6861 12.5987 11.7956 12.7228 11.8832 12.8834C11.9781 13.0513 12.0219 13.2483 12.0219 13.4746C12.0219 13.8104 11.8905 14.0878 11.6277 14.2921C11.3649 14.4965 11.0219 14.6059 10.5986 14.6059Z" fill="white"/></svg>',
  CSV: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.60465 0H9.29386L14.9598 5.9112V17.3953C14.9598 18.833 13.7928 20 12.3552 20H2.60465C1.16702 20 0 18.833 0 17.3953V2.60465C0 1.16702 1.16702 0 2.60465 0Z" fill="#00733B"/><path opacity="0.302" fill-rule="evenodd" clip-rule="evenodd" d="M9.2854 0V5.86045H14.9598L9.2854 0Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M4.40588 14.6729H7.06127V15.6793H4.40588V14.6729ZM7.89003 9.53125H10.5539V10.5291H7.89003V9.53125ZM4.40588 9.53125H7.06127V10.5291H4.40588V9.53125ZM7.89003 11.2226H10.5539V12.2289H7.89003V11.2226ZM4.40588 11.2226H7.06127V12.2289H4.40588V11.2226ZM7.89003 12.9816H10.5539V13.9879H7.89003V12.9816ZM4.40588 12.9816H7.06127V13.9879H4.40588V12.9816ZM7.89003 14.6729H10.5539V15.6793H7.89003V14.6729Z" fill="white"/></svg>',
  TXT: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.60369 0H9.29034L14.9575 5.90705V17.3963C14.9575 18.8356 13.7931 20 12.3588 20H2.60369C1.16441 20 0 18.8356 0 17.3963V2.60369C0 1.16441 1.16439 0 2.60369 0Z" fill="#251D36"/><path opacity="0.302" fill-rule="evenodd" clip-rule="evenodd" d="M9.2854 0V5.86207H14.9576L9.2854 0Z" fill="white"/><path d="M3.77813 14.6019V11.7584H2.73865V10.9688H5.74216V11.7584H4.70768V14.6019H3.77813ZM9.12047 14.6019H8.14596L7.48129 13.4875L6.81661 14.6019H5.8371L6.99153 12.6629L5.98205 10.9688H6.95656L7.48129 11.8483L8.00104 10.9688H8.98055L7.97107 12.6679L9.12047 14.6019ZM10.2499 14.6019V11.7584H9.21541V10.9688H12.2189V11.7584H11.1794V14.6019H10.2499Z" fill="white"/></svg>'
}

export const getFileIconSvg = (fileType = ''): string => {
  const normalized = fileType.toUpperCase()
  const aliases: Record<string, string> = {
    [ArtifactFileType.PPT]: ArtifactFileType.PPTX,
    [ArtifactFileType.DOC]: ArtifactFileType.DOCX,
    [ArtifactFileType.XLS]: ArtifactFileType.XLSX
  }
  return fileIconSvgs[aliases[normalized] || normalized] || fileIconSvgs[ArtifactFileType.TXT]
}

const downloadIconSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'

/*
 * Builds the "artifact card" markup shown for AI-generated files (e.g. via code interpreter) —
 * a file icon, name, type badge, and authenticated download button. Shared by both artifact
 * detection paths in `renderer.link` below so the markup/escaping/tooltip logic lives in one
 * place instead of being duplicated and risking the two copies drifting out of sync.
 */
const buildArtifactCardHtml = (fileName: string, downloadUrl: string): string => {
  // Leave the extension undefined when it can't be determined rather than defaulting to DOC —
  // silently assuming "Word document" for a file of unknown type is misleading (e.g. a PDF whose
  // name couldn't be extracted would otherwise render with a Word icon/label). getFileIconSvg
  // already falls back to a neutral generic icon when passed an unrecognized/empty type.
  const fileExtension = fileName.includes('.')
    ? fileName.split('.').pop()?.toUpperCase()
    : undefined
  const iconSvg = getFileIconSvg(fileExtension || '')
  const escapedFileName = escapeHtml(fileName)
  const escapedFileExtension = escapeHtml(fileExtension || 'FILE')
  const escapedDownloadUrl = escapeHtml(downloadUrl)

  return `
    <div class="artifact-block">
      <div class="artifact-card">
        <div class="artifact-icon">
          ${iconSvg}
        </div>
        <div class="artifact-info">
          <span class="artifact-filename" title="${escapedFileName}">${escapedFileName}</span>
          <span class="artifact-filetype">${escapedFileExtension}</span>
        </div>
        <button class="artifact-download-btn" data-url="${escapedDownloadUrl}" data-filename="${escapedFileName}" title="Download">
          ${downloadIconSvg}
        </button>
      </div>
    </div>`
}

export const getFileSize = (bytes: any, decimals: any = 2) => {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const pickRandomMessage = (dbArray: any) => {
  const index = Math.round(Math.random() * (dbArray?.length - 1))
  return dbArray[index]
}

/**
 * Extracts the "content" field value from a potentially incomplete JSON string
 * being built up character by character from streaming chunks.
 * Returns null if the content field hasn't been found yet.
 */
export const extractContentFromJsonStream = (accumulated: string): string | null => {
  // Only attempt extraction if the response looks like JSON
  if (!accumulated.trimStart().startsWith('{')) return null

  // Try full JSON parse first (for when the stream is complete)
  try {
    const parsed = JSON.parse(accumulated)
    if (parsed && typeof parsed.content === 'string') {
      return parsed.content
    }
  } catch {
    // Not complete JSON yet, try partial extraction
  }

  // Find the start of the content value
  const contentKeyPattern = /"content"\s*:\s*"/
  const match = accumulated.match(contentKeyPattern)
  if (!match || match.index === undefined) {
    return null
  }

  const valueStart = match.index + match[0].length
  const valueStr = accumulated.substring(valueStart)

  // Parse the partial JSON string value, handling escapes
  let result = ''
  let i = 0
  while (i < valueStr.length) {
    const ch = valueStr[i]
    if (ch === '"') break // End of string value
    if (ch === '\\' && i + 1 < valueStr.length) {
      const next = valueStr[i + 1]
      switch (next) {
        case '"':
          result += '"'
          i += 2
          continue
        case '\\':
          result += '\\'
          i += 2
          continue
        case 'n':
          result += '\n'
          i += 2
          continue
        case 'r':
          result += '\r'
          i += 2
          continue
        case 't':
          result += '\t'
          i += 2
          continue
        case '/':
          result += '/'
          i += 2
          continue
        case 'u':
          if (i + 5 < valueStr.length) {
            const hex = valueStr.substring(i + 2, i + 6)
            result += String.fromCharCode(parseInt(hex, 16))
            i += 6
            continue
          }
          return result // Incomplete unicode escape, return what we have
        default:
          result += ch
          i++
          continue
      }
    }
    result += ch
    i++
  }

  return result
}

/**
 * Transform AG UI protocol tool calls to ReasoningTrace format
 * Maps tool names to UI-friendly display types and determines status
 */
export const transformToolCallsForDisplay = (toolCalls: any[] = []): any[] => {
  if (!toolCalls || toolCalls.length === 0) return []

  // Map of tool names to display types
  const toolTypeMap: Record<string, string> = {
    QueryDocument: 'databox',
    GetQuery: 'dataproduct',
    ExecuteQuery: 'dataproduct',
    WebSearch: 'search',
    web_search: 'search',
    search: 'search',
    // Code Interpreter MCP server tool
    execute_code: 'code',
    executeCode: 'code',
    code_interpreter: 'code',
    codeInterpreter: 'code',
    read_file: 'read',
    readFile: 'read',
    grep_search: 'grep',
    grepSearch: 'grep',
    semantic_search: 'search',
    semanticSearch: 'search',
    edit_file: 'edit',
    editFile: 'edit',
    manage_todo: 'todo',
    manageTodo: 'todo',
    save_memory: 'memory',
    saveMemory: 'memory',
    list_memories: 'memory',
    listMemories: 'memory',
    update_memory: 'edit',
    updateMemory: 'edit',
    delete_memory: 'delete',
    deleteMemory: 'delete',
    // Frontend tools
    launchApp: 'navigate',
    openSidePanel: 'panel',
    closeSidePanel: 'panel',
    applyDataFilter: 'search',
    create_visualization: 'chart'
  }

  return toolCalls.map((toolCall, index) => {
    // Determine tool type from name. Unrecognized tools (e.g. a new/renamed MCP tool the
    // frontend hasn't been taught about yet) default to a neutral 'tool' type rather than
    // 'search' — forcing every unknown tool into 'search' previously caused the thinking
    // indicator to show the misleading "Accessing web search..." message for tools that have
    // nothing to do with search (e.g. upload_to_personaldatabox). With type 'tool', ChatBubble's
    // getThinkingMessage() falls through to its generic "Calling {action}..." message instead.
    const toolType = toolTypeMap[toolCall.name] || 'tool'

    // Parse arguments to get context
    let context: string
    let details: string
    try {
      const args =
        typeof toolCall.arguments === 'string'
          ? JSON.parse(toolCall.arguments || '{}')
          : toolCall.arguments || {}
      // Extract relevant context based on tool type
      if (args.query) context = args.query
      else if (args.filePath) context = args.filePath
      else if (args.term) context = args.term
      else if (args.url) context = args.url
      else context = toolCall.name

      // Store full arguments as details
      details =
        typeof toolCall.arguments === 'string'
          ? toolCall.arguments
          : JSON.stringify(toolCall.arguments || {})
    } catch (e) {
      context = toolCall.name
      details =
        typeof toolCall.arguments === 'string'
          ? toolCall.arguments
          : JSON.stringify(toolCall.arguments || {})
    }

    // TOOL_CALL_END only means argument streaming finished; execution continues until a result
    // arrives. A newer call supersedes an unfinished one, so only the latest call can be running.
    const status =
      toolCall.result !== undefined || index < toolCalls.length - 1 ? 'completed' : 'running'

    return {
      id: toolCall.id,
      type: toolType,
      reasoning: toolCall.reasoning || '',
      seq: toolCall.seq,
      // Convert camelCase (openSidePanel) and snake_case (upload_to_personaldatabox) tool
      // names into a readable action label ("open Side Panel", "upload To Personaldatabox")
      action: toolCall.name
        .replace(/([A-Z])/g, ' $1')
        .replace(/_(\w)/g, (_match: string, letter: string) => ` ${letter.toUpperCase()}`)
        .trim(),
      context,
      details,
      result: toolCall.result || undefined, // Include result if available
      status,
      isFrontend: toolCall.isFrontend || false
    }
  })
}

export const areArraysEqual = (arr1: any, arr2: any) => {
  arr1 = arr1 || []
  arr2 = arr2 || []
  if (arr1.length !== arr2.length || arr1.length === 0 || arr2.length === 0) {
    return false
  }

  for (let i = 0; i < arr1.length; i++) {
    if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
      return false
    }
  }
  return true
}

export const hasImages = (files: any) => {
  return files.some((file: any) => hasImageFile(file.name))
}

export const hasImageFile = (fileName: string) => {
  return validImageInput.includes(`.${getFileType(fileName)?.toLowerCase()}`)
}

export const getImageCount = (files: any) => {
  return files.filter((file: any) =>
    validImageInput.includes(`.${getFileType(file.name)?.toLowerCase()}`)
  ).length
}

export const isTempMessageId = (id?: string | null) => !!id && id.startsWith('temp-')

export const isAbortedContent = (content?: string | null) =>
  !!content && content.includes(requestAbortedMsg)

export const hasStableAssistantChild = (
  messages: ChatRawMessageItem[],
  parentId?: string | null
) => {
  if (!parentId) return false
  return messages.some(
    (msg) =>
      msg.parentId === parentId &&
      msg.role === 'assistant' &&
      !!msg.id &&
      !isTempMessageId(msg.id) &&
      !msg.content?.includes(requestAbortedMsg)
  )
}

export const getLatestAssistantChildId = (
  messages: ChatRawMessageItem[],
  parentId?: string | null
) => {
  if (!parentId) return null
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    if (msg.parentId === parentId && msg.role === 'assistant' && !!msg.id) {
      return msg.id
    }
  }
  return null
}

export const normalizeInterruptedBranches = (messages: ChatRawMessageItem[]) => {
  const normalized = messages.map((msg) => ({ ...msg }))
  const userChildrenByParent = normalized.reduce((map, msg) => {
    // Root-level (parentId === null) messages must still be grouped - excluding them here
    // skipped this repair for the very first message of a chat (abort-then-resend on message 1).
    if (msg.role !== 'user' || !msg.id) return map
    const key = msg.parentId
    if (!map.has(key)) {
      map.set(key, [])
    }
    map.get(key)!.push(msg)
    return map
  }, new Map<string | null, ChatRawMessageItem[]>())

  userChildrenByParent.forEach((siblings) => {
    if (siblings.length < 2) return

    for (let i = 1; i < siblings.length; i++) {
      const previousSibling = siblings[i - 1]
      const currentSibling = siblings[i]

      // If previous user query did not produce a stable assistant response,
      // keep the next user query in a linear chain instead of creating a version branch.
      if (!hasStableAssistantChild(normalized, previousSibling.id)) {
        const previousAssistantId = getLatestAssistantChildId(normalized, previousSibling.id)
        currentSibling.parentId =
          previousAssistantId || previousSibling.id || currentSibling.parentId
      }
    }
  })

  return normalized
}

// This function formats raw chat messages into a structured format suitable for display in a chat interface.
export const formatChatMessages = (
  rawMessages: ChatRawMessageItem[],
  messages: ChatBubbleItem[] = [],
  returnAll: boolean = false,
  isInitialLoad: boolean = false
): ChatBubbleItem[] => {
  //clean up messages by removing any responses that have the requestAbortedMsg content, and adjusting activeResponseIndex accordingly
  const cleanMessages = messages.map((msg) => {
    const filteredResponses = msg.response.filter(
      (res) => res.content && !res.content.includes(requestAbortedMsg)
    )
    const safeResponses = filteredResponses.length > 0 ? filteredResponses : msg.response.slice(-1)
    const lastResponseLen = safeResponses.length - 1
    return {
      ...msg,
      activeResponseIndex:
        msg.activeResponseIndex > lastResponseLen ? lastResponseLen : msg.activeResponseIndex,
      response: safeResponses
    }
  })
  const normalizedRawMessages = normalizeInterruptedBranches(rawMessages)
  // Group messages by parentId using reduce.
  const groups = normalizedRawMessages.reduce((map, message) => {
    const parentId = message.parentId ?? null
    if (!map.has(parentId)) {
      map.set(parentId, [])
    }
    map.get(parentId)!.push({ ...message, resources: message.resources || [] })
    return map
  }, new Map<string | null, ChatRawMessageItem[]>())

  // Convert grouped messages into the desired format.
  const formattedMessages = Array.from(groups, ([parentId, childMessages]) => {
    const currParentObject: ChatBubbleItem | undefined = cleanMessages.find(
      (msg) => msg.parentId === parentId
    )
    const currResponseLength: number = currParentObject?.response?.length || 0
    const lastKnownId = currParentObject?.response[currResponseLength - 1]?.id
    const isMsgGenerating: boolean =
      lastKnownId === null || (typeof lastKnownId === 'string' && lastKnownId.startsWith('temp-'))
    const activeResponseIndex: number = (() => {
      if (isInitialLoad || isMsgGenerating) return childMessages.length - 1
      return Math.min(currParentObject?.activeResponseIndex || 0, childMessages.length - 1)
    })()

    const childMsgResources = childMessages
      .flatMap((msg) => msg.resources)
      .filter((resource, index, self) => index === self.findIndex((r) => r.id === resource.id))
      .map((res: any) => ({ ...res, type: 'file' }))

    const childMsgImages = childMessages
      .flatMap((msg) => msg.images || [])
      .map((img: any) => ({ ...img, type: 'image' }))

    return {
      ...chatMessageSchema,
      parentId,
      activeResponseIndex,
      response: childMessages.map((msg) => ({
        ...chatMessageSchema.response[0],
        id: msg.id,
        agentId: msg.agentId,
        content: msg.content,
        citations: msg.citations || [],
        modelVersion: msg.modelVersion,
        runId: msg.runId || null,
        toolCalls: msg.toolCalls || [],
        reasonings: msg.reasonings || [],
        elapsedTime: msg.elapsedTime || 0,
        reasoningContent: msg.reasoningContent || '',
        aborted: msg.aborted === true
      })),
      resources: [...childMsgResources, ...childMsgImages],
      role: childMessages[childMessages.length - 1]?.role,
      type: childMessages[childMessages.length - 1]?.type || 'text'
    }
  })

  // If there are no messages, return an empty array or the formatted messages based on the returnAll flag.
  return returnAll
    ? formattedMessages
    : resetLinkedMessages(formattedMessages, messages.length === 0)
}

// This function resets the linked messages based on the active response index and ensures that the last message always shows its most recent response.
const resetLinkedMessages = (
  formattedMessages: ChatBubbleItem[],
  isInitialLoad: boolean
): ChatBubbleItem[] => {
  if (formattedMessages.length === 0) return []

  const finalMessages: ChatBubbleItem[] = []
  let currentMessage = formattedMessages[0]

  // Determine the starting activeResponseIndex for the first message.
  let currResponseIndex =
    isInitialLoad ||
    currentMessage.response[currentMessage.response.length - 1]?.id === null ||
    currentMessage.response[currentMessage.response.length - 1]?.id?.startsWith('temp-')
      ? currentMessage.response.length - 1
      : currentMessage.activeResponseIndex
  finalMessages.push({ ...currentMessage, activeResponseIndex: currResponseIndex })

  // Traverse and update linked messages
  while (true) {
    // A child can be parented to any sibling response id here, not just the active one.
    const activeId = currentMessage.response[currResponseIndex]?.id || undefined
    const candidateIds = [
      activeId,
      ...currentMessage.response.map((r) => r.id).filter((id) => id && id !== activeId)
    ].filter((id): id is string => !!id)

    const nextMessage = candidateIds
      .map((id) => formattedMessages.find((msg) => msg.parentId === id))
      .find((msg): msg is ChatBubbleItem => !!msg)
    if (!nextMessage) break

    const nextResponse = nextMessage.response[nextMessage.response.length - 1]
    // Determine the activeResponseIndex for the next message.
    // If the next response is generating or has a temporary ID, set it to the last response.
    const activeResponseIndex =
      nextResponse?.id === null || nextResponse?.id.startsWith('temp-')
        ? nextMessage.response.length - 1
        : nextMessage.activeResponseIndex
    finalMessages.push({ ...nextMessage, activeResponseIndex })
    currentMessage = nextMessage
    currResponseIndex = activeResponseIndex
  }

  // Ensure the last message always shows its most recent response.
  if (finalMessages.length) {
    const lastIndex = finalMessages.length - 1
    finalMessages[lastIndex].activeResponseIndex = finalMessages[lastIndex].response.length - 1
  }

  return finalMessages
}

// This function navigates through the chat messages based on the selected index and message parent ID.
export const navigateMsgResponse = (
  preMessages: ChatBubbleItem[],
  rawMessages: ChatRawMessageItem[],
  messageParentId: string | null,
  selectedIndex: number,
  setMessages: any
) => {
  const postMessages: ChatBubbleItem[] = []
  const allFormattedMessages: ChatBubbleItem[] = formatChatMessages(rawMessages, preMessages, true)

  let currentParentId = messageParentId
  let isFirstIteration = true

  //This loop finds all messages that are linked to the current message by parentId.
  while (true) {
    const foundMessage = allFormattedMessages.find((msg) => msg.parentId === currentParentId)
    if (!foundMessage) break

    const activeResponseIndex = isFirstIteration ? selectedIndex : foundMessage.response.length - 1
    postMessages.push({ ...foundMessage, activeResponseIndex })

    // Fall back to any sibling response id so the child stays visible regardless of selected version.
    const activeId = foundMessage.response[activeResponseIndex]?.id || undefined
    const candidateIds = [
      activeId,
      ...foundMessage.response.map((r) => r.id).filter((id) => id && id !== activeId)
    ].filter((id): id is string => !!id)
    // Don't default to null on no match - null also matches the root message and loops forever.
    const nextParentId = candidateIds.find((id) =>
      allFormattedMessages.some((msg) => msg.parentId === id)
    )
    if (!nextParentId) break
    currentParentId = nextParentId
    isFirstIteration = false
  }

  setMessages([...preMessages, ...postMessages])
}

const escapeMarkdownTableCell = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value).replace(/\|/g, '\\|').replace(/\r?\n/g, ' ')
}

// Builds a Markdown table string from a table payload. Handles both the live-streaming
// TOOL_TABLE_CONTENT shape (`data` / `resultSetMetaData` directly on the table object) and the
// persisted/history `events[].type === 'table'` shape (nested under `result_set`).
const buildTableMarkdown = (table: any): string => {
  const resultSet = table?.result_set ?? table
  const rows: any[][] = Array.isArray(resultSet?.data) ? resultSet.data : []
  const columns: string[] =
    resultSet?.resultSetMetaData?.rowType?.map((col: any) => col?.name || '') || []
  if (rows.length === 0 || columns.length === 0) return ''
  const headerRow = `| ${columns.map(escapeMarkdownTableCell).join(' | ')} |`
  const separatorRow = `| ${columns.map(() => '---').join(' | ')} |`
  const bodyRows = rows
    .map((row) => `| ${row.map(escapeMarkdownTableCell).join(' | ')} |`)
    .join('\n')
  const heading = table?.title ? `**${escapeMarkdownTableCell(table.title)}**\n\n` : ''
  return `${heading}${headerRow}\n${separatorRow}\n${bodyRows}`
}

// Persisted/history messages don't embed tool tables as Markdown in `content` — the table data
// only lives inside `events[].raw.table` entries (`events[].type === 'table'`). Extract and
// convert those into Markdown so they render the same way as live-streamed tables.
const extractTableMarkdownFromEvents = (events: any[]): string[] => {
  if (!Array.isArray(events)) return []
  return events
    .filter((event) => event?.type === 'table')
    .map((event) => buildTableMarkdown(event?.raw?.table))
    .filter(Boolean)
}

// Persisted/history messages carry charts as `events[].type === 'chart'` entries (a Vega-Lite
// spec stringified at `raw.chart.chart_spec`), never as a `create_visualization` tool call.
// Synthesize one so it renders through ChatBubble's existing chartSpecs pipeline.
const extractChartToolCallsFromEvents = (events: any[]): any[] => {
  if (!Array.isArray(events)) return []
  return events
    .filter((event) => event?.type === 'chart')
    .map((event) => {
      const rawChart = event?.raw?.chart
      if (!rawChart) return null
      let vegaSpec = rawChart.chart_spec
      if (typeof vegaSpec === 'string') {
        if (!isValidJSON(vegaSpec)) return null
        vegaSpec = JSON.parse(vegaSpec)
      }
      const chartSpec = convertVegaLiteToChartSpec(vegaSpec)
      if (!chartSpec) return null
      return {
        id: rawChart.tool_use_id || `chart-${Math.random().toString(36).slice(2)}`,
        name: 'create_visualization',
        arguments: JSON.stringify(chartSpec),
        ended: true
      }
    })
    .filter(Boolean)
}

// Persisted history stores tool calls as Anthropic blocks (`tool_use_id`/`input`) with results in
// a separate `toolResults` array, not the streaming {id, arguments, result} shape UI code expects.
const normalizePersistedToolCalls = (toolCalls: any, toolResults: any): any[] => {
  if (!Array.isArray(toolCalls) || toolCalls.length === 0) return toolCalls || []

  const resultsByToolUseId = new Map<string, any>()
  if (Array.isArray(toolResults)) {
    toolResults.forEach((result) => {
      const key = result?.tool_use_id || result?.id
      if (key) resultsByToolUseId.set(key, result)
    })
  }

  return toolCalls.map((toolCall) => {
    // Already in the normalized {id, arguments} shape (e.g. streamed or synthesized chart calls).
    if (toolCall?.arguments !== undefined) return toolCall

    const id = toolCall?.tool_use_id || toolCall?.id
    const matchedResult = resultsByToolUseId.get(id)

    return {
      ...toolCall,
      id,
      arguments: JSON.stringify(toolCall?.input || {}),
      result: matchedResult ? JSON.stringify({ content: matchedResult.content }) : undefined,
      ended: true
    }
  })
}

export const getMessageContent = (content: string): any => {
  let messageContent: any = isValidJSON(content) ? JSON.parse(content) : { content }

  // Persisted assistant messages store a JSON-encoded response envelope in the content property.
  // Unwrap it so content, tool calls, citations, and reasoning remain available after history loading.
  while (
    messageContent &&
    typeof messageContent.content === 'string' &&
    isValidJSON(messageContent.content)
  ) {
    const nestedContent = JSON.parse(messageContent.content)
    if (!nestedContent || typeof nestedContent !== 'object' || Array.isArray(nestedContent)) break
    messageContent = { ...messageContent, ...nestedContent }
  }

  // Persisted/history tool tables and charts only live in `events`, not inline in `content` —
  // fold their Markdown/tool-call equivalents in so they render the same as live-streamed messages.
  const tableMarkdownBlocks = extractTableMarkdownFromEvents(messageContent?.events)
  const chartToolCalls = extractChartToolCallsFromEvents(messageContent?.events)
  const hasPersistedToolCalls =
    Array.isArray(messageContent?.toolCalls) && messageContent.toolCalls.length > 0

  if (tableMarkdownBlocks.length === 0 && chartToolCalls.length === 0 && !hasPersistedToolCalls) {
    return messageContent
  }

  const normalizedToolCalls = hasPersistedToolCalls
    ? normalizePersistedToolCalls(messageContent.toolCalls, messageContent.toolResults)
    : messageContent?.toolCalls || []

  return {
    ...messageContent,
    content:
      tableMarkdownBlocks.length > 0
        ? [messageContent.content, ...tableMarkdownBlocks].filter(Boolean).join('\n\n')
        : messageContent.content,
    ...((hasPersistedToolCalls || chartToolCalls.length > 0) && {
      toolCalls: [...normalizedToolCalls, ...chartToolCalls]
    })
  }

  return messageContent
}

export const formatMarkdown = (
  rawContent: string,
  hasLoader: boolean = false,
  citations: any[] = [],
  isDrawerMode?: boolean
): string => {
  const drawerMode = isDrawerMode ?? checkIsDrawerMode()
  const renderer: any = new marked.Renderer()
  const dompurifyConfig = {
    ADD_ATTR: [
      'target',
      'class',
      'onclick',
      'style',
      'onmouseenter',
      'data-url',
      'data-filename',
      'data-inline-citation-index',
      'data-citation-file-name',
      'data-page-number',
      'data-lang'
    ]
  }

  const languageMapping: any = {
    excel: 'excel-formula',
    asp: 'csharp',
    php: 'python'
  }

  renderer.link = function ({ href, text, title }: any) {
    if (href?.startsWith('citation-preview:')) {
      const [, citationIndex, pageNumber, encodedFileName] = href.split(':')
      const citationFileName = encodedFileName ? decodeURIComponent(encodedFileName) : ''
      // Footnote-style marker (e.g. "[15]") instead of the full "{fileName} p. {pageNumber}" label —
      // the file name still travels via data-citation-file-name/aria-label for the click handler/a11y.
      return `<button type="button" class="citation-marker align-super text-[0.75em] text-primary bg-transparent border-0 p-0 m-0 font-inherit cursor-pointer" data-inline-citation-index="${escapeHtml(
        citationIndex
      )}" data-citation-file-name="${escapeHtml(citationFileName)}" data-page-number="${escapeHtml(
        pageNumber
      )}" aria-label="Preview ${escapeHtml(citationFileName)}, page ${escapeHtml(
        pageNumber
      )}">[${escapeHtml(pageNumber)}]</button>`
    }

    // Try multiple matching strategies to find citation
    let citation = citations.find((c) => c.reference === href || c.url === href)

    // If no exact match, try domain-based matching using fetchDomain
    if (!citation) {
      const hrefDomain = fetchDomain(href)
      if (hrefDomain) {
        citation = citations.find((c) => {
          const citationUrl = c.url || c.reference
          if (!citationUrl) return false
          const citationDomain = fetchDomain(citationUrl)
          return hrefDomain === citationDomain
        })
      }
    }

    // When the AI generates a file (e.g. via code interpreter), the backend returns it as a
    // citation with type "doc" and a file name. Instead of rendering a plain hyperlink, show
    // a downloadable artifact card with the file icon, name, and an authenticated download button.
    if (citation && citation.type?.toLowerCase() === 'doc' && citation.name) {
      const downloadUrl = citation.url || citation.reference || href
      return buildArtifactCardHtml(citation.name, downloadUrl)
    }

    // Detect document download URLs — works whether or not a citation matched
    // Pattern: .../documents/{guid}/download
    const documentDownloadPattern =
      /\/documents\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/download/i
    if (documentDownloadPattern.test(href)) {
      // Determine filename: prefer citation name, then link text if it has a file extension, then extract from rawContent
      const fileExtPattern = /\.\w{2,5}$/
      const fileName =
        citation?.name ||
        (fileExtPattern.test(text) ? text : null) ||
        (() => {
          const urlIndex = rawContent.indexOf(href)
          const textBeforeUrl = urlIndex >= 0 ? rawContent.substring(0, urlIndex) : rawContent

          // Only look at nearby text (last 300 chars before the URL) to avoid
          // matching filenames from unrelated sections when multiple files are listed
          const nearbyText = textBeforeUrl.slice(-300)

          // Backtick-wrapped filenames (e.g. "the file name is `report.pdf`") are an explicit,
          // deliberate callout by the model — a much stronger signal than plain in-paragraph
          // proximity. Use the closest one directly, bypassing the paragraph-separation
          // heuristic below (which exists only to avoid misattributing an unrelated file's
          // name when the filename isn't clearly called out). Without this, structured
          // responses that state the filename under one heading (e.g. "### File name") and the
          // download link under another (e.g. "### Download") get treated as "separated" and
          // fall back to a generic "document.<ext>" name even though the real name is right there.
          const codeSpanMatches = [
            ...nearbyText.matchAll(/`([\w][\w\-.]*\.(?:pptx?|docx?|xlsx?|csv|pdf|txt))`/gi)
          ]
          if (codeSpanMatches.length > 0) return codeSpanMatches[codeSpanMatches.length - 1][1]

          // Try filenames in parentheses like (report.pptx)
          const parenMatches = [...nearbyText.matchAll(/\(([^)\s]+\.\w{2,5})\)/g)]
          if (parenMatches.length > 0) return parenMatches[parenMatches.length - 1][1]

          // Collect all document filenames mentioned in nearby text
          const docExtRegex = /\b([\w][\w\-.]*\.(?:pptx?|docx?|xlsx?|csv|pdf|txt))\b/gi
          const allFilenames = [...nearbyText.matchAll(docExtRegex)]

          // Context-aware type hints: map keywords to expected file extensions. Kept specific
          // (e.g. "word document" rather than the bare word "word") so ordinary English usage
          // elsewhere in the message can't be mistaken for a file-type callout.
          const typeHints: [RegExp, string][] = [
            [/\b(powerpoint|presentation|pptx?|slides?)\b/, 'pptx'],
            [/\b(excel|spreadsheet|xlsx?)\b/, 'xlsx'],
            [/\b(ms\s*word|word\s*document|docx?)\b/, 'docx'],
            [/\b(csv)\b/, 'csv'],
            [/\b(pdf)\b/, 'pdf'],
            [/\b(text\s*file|\.txt)\b/, 'txt']
          ]

          if (allFilenames.length > 0) {
            // Use the last (closest-to-URL) filename, unless it's clearly from
            // a different section (separated by multiple paragraph breaks)
            const lastMatch = allFilenames[allFilenames.length - 1]
            const lastPos = nearbyText.lastIndexOf(lastMatch[1])
            const textAfter = nearbyText.substring(lastPos + lastMatch[1].length)
            const isSeparated = /\n\n[\s\S]+\n\n/.test(textAfter)

            if (!isSeparated) return lastMatch[1]
            // Separated — fall through to keyword-only inference below
          }

          // No closely-associated filename — infer extension from type keywords mentioned
          // anywhere earlier in the message, not just the narrow nearby-text window used for
          // filename matching above. A heading like "### PDF Report Created" near the top of a
          // longer message (e.g. after several bullet points before "Download it here:") is a
          // reliable type signal even when it falls outside that window — without this, such
          // messages fell back to the generic 'document' name and rendered the wrong file icon.
          const fullTextLower = textBeforeUrl.toLowerCase()
          for (const [contextPattern, ext] of typeHints) {
            if (contextPattern.test(fullTextLower)) return `document.${ext}`
          }

          return 'document'
        })()
      const downloadUrl = citation?.url || citation?.reference || href
      return buildArtifactCardHtml(fileName, downloadUrl)
    }

    let icon: any = null

    // Generate unique ID for this citation to update DOM later
    const citationId = `citation-${Date.now()}-${Math.random().toString(36)}`

    if (citation && (citation.url || citation.reference)) {
      const domain = fetchDomain(citation?.url || citation?.reference)
      icon = domain ? `https://${domain}/favicon.ico` : null

      // Asynchronously validate favicon and update DOM after render
      if (icon) {
        setTimeout(async () => {
          try {
            const isValid = await validateImageUrl(icon)

            const citationElement = document.getElementById(citationId)
            const iconElement = citationElement?.querySelector('.citation-icon')

            if (iconElement && isValid) {
              iconElement.innerHTML = `<img src="${icon}" alt="${escapeHtml(
                text
              )}" width="20" class="max-h-[24px] p-0 rounded-full border !border-[var(--gray-300)]"/>`
            }
            // If not valid, keep the default SVG globe icon
          } catch (error) {
            console.warn('Failed to validate favicon:', error)
          }
        }, 0)
      }
    }

    return `
      <span class="badge-neutral-pill group relative" id="${citationId}"
            onmouseenter="
              //get the hover card element within this wrapper
              const card = this.querySelector('.badge-hover-card');
              
              //get the position of the citation link wrapper
              const rect = this.getBoundingClientRect();
              
              // Get the actual card width and account for potential container padding
              const cardWidth = ${drawerMode} ? 200 : 450; // actual card width
              const containerPadding = 100; // buffer for container padding/margins
              const rightEdge = rect.right + cardWidth + containerPadding;
              
              //get the current viewport width to check for overflow
              const viewportWidth = window.innerWidth;
              
              //check if the card would overflow the viewport on the right side
              if (rightEdge > viewportWidth) {
                //not enough space on the right -> position card on the left side
                card.style.left = 'auto';    
                card.style.right = '0';
              } else {
                //enough space on the right -> position card normally on the right side
                card.style.left = '0';
                card.style.right = 'auto';
              }
              
              // Check vertical space and position card accordingly
              const cardHeight = 300; // height of the hover card + buffer for margin/padding
              const bottomEdge = rect.bottom + cardHeight;
              const viewportHeight = window.innerHeight;
              
              if (bottomEdge > viewportHeight) {
                // Not enough space at bottom -> position card on top
                card.style.top = 'auto';
                card.style.bottom = '100%';
                card.style.marginBottom = '0';
                card.style.marginTop = '0';
              } else {
                // Enough space at bottom -> position card normally below
                card.style.top = '100%';
                card.style.bottom = 'auto';
                card.style.marginTop = '0';
                card.style.marginBottom = '0';
              }
            ">
        <a href="${escapeHtml(
          href
        )}" target="_blank" rel="noopener noreferrer" class="badge-neutral-pill-link">${text}</a>
        <span class="badge-hover-card ${
          drawerMode ? 'badge-hover-card--narrow' : 'badge-hover-card--wide'
        }" onclick="window.open('${escapeForInlineJsAttr(href)}', '_blank')">
          <span class="mb-1">
             <span class="citation-icon">
               <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="var(--color-primary)" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" class="feather feather-globe"><circle cx="10" cy="10" r="8.33"/><line x1="1.67" y1="10" x2="18.33" y2="10"/><path d="M10 1.67a12.75 12.75 0 0 1 0 16.66M10 1.67a12.75 12.75 0 0 0 0 16.66"/></svg>
             </span>
            <span class="text-[var(--gray-500)] text-sm ml-1">${
              citation?.name ? escapeHtml(citation.name) : text || ''
            }</span>
          </span>
          <span class="block leading-5">
            <span class="block text-[var(--gray-800)] font-semibold my-50">${escapeHtml(
              citation?.title || ''
            )}</span>
            <span class="mt-[5px] text-[var(--gray-500)] leading-6 overflow-hidden text-ellipsis block" style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${escapeHtml(
              citation?.preview || ''
            )}</span>
          </span>
        </span>
      </span>
    `
  }

  renderer.code = function ({ text, lang }: any) {
    const prismLang = languageMapping[lang] || lang
    const highlighted = Prism.highlight(
      text,
      Prism.languages[prismLang] || Prism.languages.markup,
      prismLang
    )
    const escapedLanguage = escapeHtml(lang || '')

    return `
   <div class="code-block">
   <div class="code-header">
   <span class="language">${escapedLanguage}</span>
   <div class="flex items-center">
   <div class="tooltip-custom">
   <span class="maximize-button">${maximizeIconString}</span>
   <span class="tooltiptext-custom">Maximize</span>
   </div>
   <div class="tooltip-custom">
   <span class="copy-button">${copyIconString}</span>
   <span class="tooltiptext-custom">Copy</span>
   </div>
   <div class="tooltip-custom">
   <span class="download-button" data-lang="${escapedLanguage}">${downloadIconString}</span>
   <span class="tooltiptext-custom">Download</span>
   </div>
   </div>
   </div>
   <pre class="language-${escapedLanguage}"><code class="language-${escapedLanguage}">${highlighted}</code></pre>
   </div>`
  }

  renderer.table = function ({ header, rows }: any) {
    // Use parseInline (not cell.text) so inline markdown - bold, links, code, etc. - inside
    // table cells is rendered as HTML instead of showing the raw markdown syntax.
    const headerCells = header
      .map((cell: any) => `<th>${this.parser.parseInline(cell.tokens)}</th>`)
      .join('')
    const bodyRows = rows
      .map(
        (row: any) =>
          `<tr>${row
            .map((cell: any) => `<td>${this.parser.parseInline(cell.tokens)}</td>`)
            .join('')}</tr>`
      )
      .join('')

    return `
   <div class="table-block">
   <div class="table-actions">
   <div class="table-scroll-buttons">
   <button class="table-action-btn table-scroll-left-button" title="Scroll left" disabled>${chevronLeftIconString}</button>
   <button class="table-action-btn table-scroll-right-button" title="Scroll right">${chevronRightIconString}</button>
   </div>
   <div class="table-actions-end">
   <button class="table-action-btn table-copy-button" title="Copy to clipboard">${copyIconString}<span>Copy</span></button>
   <div class="table-download-dropdown">
   <button class="table-action-btn table-download-toggle" title="Download">${downloadIconString}<span>Download</span><svg class="dropdown-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg></button>
   <div class="table-download-menu">
   <button class="table-download-option table-csv-button">CSV (.csv)</button>
   <button class="table-download-option table-excel-button">Excel (.xlsx)</button>
   <button class="table-download-option table-json-button">JSON (.json)</button>
   </div>
   </div>
   </div>
   </div>
   <div class="table-scroll-wrapper">
   <table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
   </div>
   </div>`
  }

  const renderKatex = (text: string) => {
    try {
      return (
        text
          //@ts-ignore
          .replace(/\\\[(.+?)\\\]/gs, (_, formula) =>
            katex.renderToString(formula.trim(), { displayMode: true })
          )
          .replace(/\\\((.+?)\\\)/g, (_, formula) =>
            katex.renderToString(formula.trim(), { displayMode: false })
          )
      )
    } catch (error) {
      console.error('Error rendering KaTeX:', error)
      return text // Return the original text if there's an error
    }
  }

  // The model emits document citations as plain text, e.g. `[Report name, p. 7]`, rather
  // than Markdown links. Convert only labels that resolve to a doc citation; unrelated square
  // brackets remain untouched. The custom scheme is consumed by renderer.link above and never
  // reaches navigation APIs.
  const inlineCitationPattern = /\[([^\]\r\n]+?),\s*p(?:age)?\.?\s*(\d+)\](?!\s*\()[,.]?/gi
  const consumedCitationIndexes = new Set<number>()
  const linkedContent = rawContent.replace(
    inlineCitationPattern,
    (fullMatch, rawFileName: string, pageNumber: string) => {
      const fileName = rawFileName.trim().replace(/[,;:\s]+$/, '')
      const normalizedLabel = normalizeCitationFileName(fileName)
      const requestedPage = Number(pageNumber)
      const matchingFileIndexes = citations.reduce<number[]>((indexes, citation, index) => {
        if (citation?.type?.toLowerCase() !== 'doc') return indexes
        const candidates = [citation.name, citation.title, citation.reference, citation.url]
        if (
          candidates.some((candidate) => normalizeCitationFileName(candidate) === normalizedLabel)
        ) {
          indexes.push(index)
        }
        return indexes
      }, [])
      const pageMatchingIndexes = matchingFileIndexes.filter((index) => {
        const citation = citations[index]
        const citationPages = [
          citation?.page,
          citation?.pageNumber,
          citation?.metadata?.page,
          citation?.metadata?.pageNumber
        ].flat()
        return citationPages.some((page) => Number(page) === requestedPage)
      })
      const citationIndex =
        pageMatchingIndexes.find((index) => !consumedCitationIndexes.has(index)) ??
        matchingFileIndexes.find((index) => !consumedCitationIndexes.has(index)) ??
        pageMatchingIndexes[0] ??
        matchingFileIndexes[0] ??
        -1

      if (citationIndex >= 0) consumedCitationIndexes.add(citationIndex)

      // Footnote-style markers (e.g. "[15][13][14]") read fine with no separating/trailing
      // punctuation, unlike the old full "{fileName} p. {pageNumber}" text label.
      return citationIndex >= 0
        ? `[${fileName} p. ${pageNumber}](citation-preview:${citationIndex}:${pageNumber}:${encodeURIComponent(
            fileName
          )})`
        : fullMatch
    }
  )

  const katexProcessed = renderKatex(linkedContent)
  const markedHtml = marked(katexProcessed, { renderer })
  // Strip leftover [filename.ext] bracket references that appear after artifact download cards
  // These occur when the backend writes e.g. "[salary_tax.pptx]." after the link that became an artifact card
  const cleanedHtml = (markedHtml as string).replace(
    /(<\/div>\s*<\/div>)\s*(?:\[([^\]]+\.\w{2,5})\]\.?)/g,
    '$1'
  )
  const sanitized = DOMPurify.sanitize(
    `${cleanedHtml}${hasLoader ? '<div class="single-pulse"></div>' : ''}`,
    dompurifyConfig
  )

  return sanitized
}
